<script lang="ts">
	import { Settings, Upload, Sparkles, Music, Video, Zap, Check, FileCode, Shield, FileText, Package } from 'lucide-svelte';
	import { useReleaseContext } from '../../context/release';
	import { getReleaseTypesForProfile, getReleaseTypeDescription } from '../../types/release';
	import type { ReleaseType, OutputFormat } from '../../types/release';

	// Phase 6: Svelte 5 callback props! 🚀
	let { onsubmit }: { onsubmit?: () => void } = $props();

	// Phase 3: Use cleaner context interface
	const { releaseConfigState } = useReleaseContext();

	// Phase 6: Reactive logic using $derived
	let isDdex = $derived(($releaseConfigState.outputFormat ?? 'ddex-ern') === 'ddex-ern');
	let validReleaseTypes = $derived(getReleaseTypesForProfile($releaseConfigState.releaseProfile, $releaseConfigState.outputFormat));
	let releaseTypeDescription = $derived(getReleaseTypeDescription($releaseConfigState.releaseType));

	// Phase 6: Use $effect to update release type when profile changes if invalid
	$effect(() => {
		if (!validReleaseTypes.includes($releaseConfigState.releaseType)) {
			$releaseConfigState.releaseType = validReleaseTypes[0];
		}
	});

	function handleSubmit() {
		onsubmit?.();
	}
</script>

<div class="space-y-8">

	<form class="space-y-8" onsubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
		<!-- Release Configuration Section -->
		<div class="card animate-slide-up">
			<div class="card-body">
				<div class="flex items-start gap-6 mb-8">
					<div class="avatar avatar-lg bg-blue-600 text-white flex-shrink-0">
						<Settings size={32} />
					</div>
					<div class="flex-1">
						<h2 class="text-3xl font-bold text-gray-900 mb-2">Release Configuration</h2>
						<p class="text-lg text-gray-600">Configure your DDEX release settings</p>
					</div>
				</div>
				
				<!-- Output Format -->
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
					<div class="form-group">
						<label for="output-format" class="form-label flex items-center gap-3 text-lg">
							<Package size={20} class="text-blue-600" />
							Output Format
						</label>
						<select
							class="form-select text-lg py-4"
							id="output-format"
							bind:value={$releaseConfigState.outputFormat}
						>
							<option value="ddex-ern">DDEX ERN</option>
							<option value="apple-itunes">Apple iTunes</option>
						</select>
						<p class="form-help text-base">The output package format for this release</p>
					</div>
				</div>

				<!-- Row 1: Profile and Release Type -->
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
					<div class="form-group">
						<label for="release-profile" class="form-label flex items-center gap-3 text-lg">
							{#if $releaseConfigState.releaseProfile === 'Audio'}
								<Music size={20} class="text-blue-600" />
							{:else}
								<Video size={20} class="text-blue-600" />
							{/if}
							Release Profile
						</label>
						<select 
							class="form-select text-lg py-4" 
							id="release-profile" 
							bind:value={$releaseConfigState.releaseProfile}
						>
							<option value="Audio">Audio</option>
							<option value="Video">Video</option>
						</select>
						<p class="form-help text-base">The profile template for this release</p>
					</div>

					<div class="form-group">
						<label for="release-type" class="form-label flex items-center gap-3 text-lg">
							<FileText size={20} class="text-blue-600" />
							Release Type
						</label>
						<select 
							class="form-select text-lg py-4" 
							id="release-type" 
							bind:value={$releaseConfigState.releaseType}
						>
							{#each validReleaseTypes as releaseType}
								<option value={releaseType}>{releaseType}</option>
							{/each}
						</select>
						<p class="form-help text-base">{releaseTypeDescription}</p>
					</div>
				</div>

				<!-- Row 2: ERN Version and AVS Version (DDEX only) -->
				{#if isDdex}
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<div class="form-group">
						<label for="ern-version" class="form-label flex items-center gap-3 text-lg">
							<FileCode size={20} class="text-blue-600" />
							DDEX ERN Version
						</label>
						<select 
							class="form-select text-lg py-4" 
							id="ern-version" 
							bind:value={$releaseConfigState.ernVersion}
						>
							<option value="43">4.3</option>
							<option value="431">4.3.1</option>
							<option value="432">4.3.2</option>
						</select>
						<p class="form-help text-base">DDEX ERN schema version to use</p>
					</div>

					<div class="form-group">
						<label for="avs-version" class="form-label flex items-center gap-3 text-lg">
							<Shield size={20} class="text-blue-600" />
							AVS Version
						</label>
						<select 
							class="form-select text-lg py-4" 
							id="avs-version" 
							bind:value={$releaseConfigState.avsVersion}
						>
							<option value={3}>3</option>
							<option value={4}>4</option>
							<option value={5}>5</option>
							<option value={6}>6</option>
							<option value={7}>7</option>
							<option value={8}>8</option>
							<option value={9} selected>9</option>
						</select>
						<p class="form-help text-base">Allowed Value Set version to validate</p>
					</div>
				</div>
				{/if}
			</div>
		</div>

		<!-- Asset Generation Section -->
		<div class="card animate-slide-up">
			<div class="card-body">
				<div class="flex items-start gap-6 mb-8">
					<div class="avatar avatar-lg bg-purple-600 text-white flex-shrink-0">
						<Sparkles size={32} />
					</div>
					<div class="flex-1">
						<h2 class="text-3xl font-bold text-gray-900 mb-2">Asset Generation</h2>
						<p class="text-lg text-gray-600">Optional AI-powered content creation</p>
					</div>
				</div>

				<div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 border border-blue-200">
					<label class="flex items-start gap-6 cursor-pointer">
						<input 
							id="generate-ai-assets"
							type="checkbox" 
							class="form-checkbox mt-1 flex-shrink-0" 
							bind:checked={$releaseConfigState.generateAiAssets} 
						/>
						<div class="flex-1">
							<div class="text-2xl font-bold text-gray-900 mb-3">Generate Assets</div>
							<div class="text-lg text-gray-700 mb-6 leading-relaxed">
								Automatically generate stunning cover art, audio content, and promotional videos using state-of-the-art AI models
							</div>
							<div class="flex items-center gap-3">
								<div class="badge badge-primary text-base px-4 py-2">
									<Sparkles size={16} />&nbsp;Powered by Google Vertex API
								</div>
							</div>
						</div>
					</label>
				</div>
			</div>
		</div>

		<!-- Submit Button -->
		<div class="flex justify-center pt-8">
			<button type="submit" class="btn btn-primary btn-lg text-xl px-16 py-6 shadow-2xl animate-scale-in">
				<Zap size={24} />
				Generate Release
			</button>
		</div>
	</form>
</div>